import { motion } from "framer-motion";
import { createContext, useContext } from "react";
import { NodeContext, useEditor } from "@nkitaa/builder-engine";
import {
  type RuntimePropsWithChildren,
  SECTION_REVEAL_PROPS,
  ThemeSectionContainer,
  classNames,
  getGridClasses,
  isVisible,
  sanitizeImageSrc,
  type BorderSide,
} from "./shared";
import { useWBLang } from "../lang/WBLanguageRuntimeProvider";
import { useWBTheme } from "../theme/WBThemeRuntimeProvider";
import { resolveText, type LangText } from "../lib/resolve-text";
import { AttachmentChips } from "./attachmentChips";
import { CustomFieldLines } from "./customFieldLines";

/** Sponsor tier → CSS `order` band. MIRRORS the editor
 *  (main-frontend children/Sponsors SPONSOR_TIER_ORDER) — keep in sync.
 *  Equal orders keep DOM order, so untiered walls render unchanged.
 *  LEGACY path: only renders when the section carries no `tierBands` prop
 *  (pages baked before configurable tiers, or events without a config). */
type SponsorTier = "none" | "gold" | "silver" | "bronze";
const TIER_ORDER: Record<SponsorTier, number> = {
  gold: 1,
  silver: 11,
  bronze: 21,
  none: 31,
};
const TIER_SCALE: Record<SponsorTier, string | undefined> = {
  gold: "scale-110",
  silver: undefined,
  bronze: "scale-90",
  none: undefined,
};
const TIER_HEADINGS: { tier: Exclude<SponsorTier, "none">; enabledKey: string; labelKey: string }[] = [
  { tier: "gold", enabledKey: "tierGoldEnabled", labelKey: "tierGoldLabel" },
  { tier: "silver", enabledKey: "tierSilverEnabled", labelKey: "tierSilverLabel" },
  { tier: "bronze", enabledKey: "tierBronzeEnabled", labelKey: "tierBronzeLabel" },
];

/**
 * Configurable tiers (T-wave): the section prop `tierBands` carries the
 * event's configured tiers in band order, baked by the editor at save time;
 * children carry `tierId` (dual-written next to the legacy `tier` value so
 * OLD per-event runtime snapshots — which know only the 4 hardcoded bands —
 * keep rendering an approximation). Band order/labels/styles come from the
 * array; an unmatched/absent tierId falls into the implicit trailing
 * "no tier" band (no heading). MIRRORED in the editor twin — keep in sync.
 */
export type SponsorTierBand = {
  id: string;
  label?: LangText;
  enabled?: boolean;
  /** prominent | standard | subtle → the legacy gold/none/bronze scale. */
  style?: string;
};

const BAND_STYLE_SCALE: Record<string, string | undefined> = {
  prominent: "scale-110",
  standard: undefined,
  subtle: "scale-90",
};

/** Dynamic band `order` math: heading i*10, logos i*10+1, no-tier trailing. */
const bandLogoOrder = (index: number) => index * 10 + 1;
const bandHeadingOrder = (index: number) => index * 10;
const noTierOrder = (bandCount: number) => bandCount * 10 + 1;

/** Children can't read section props — the section hands its parsed bands
 *  down via context (both live in this file; the editor twin mirrors). */
export const SponsorTierBandsContext = createContext<SponsorTierBand[] | null>(null);

export function parseTierBands(raw: unknown): SponsorTierBand[] | null {
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const bands = raw.filter(
    (b): b is SponsorTierBand =>
      !!b && typeof b === "object" && typeof (b as SponsorTierBand).id === "string",
  );
  return bands.length > 0 ? bands : null;
}

/** Key for the implicit no-tier bucket in the dynamic count record. */
const NO_TIER_KEY = "__none";

/**
 * Count visible children per tier band from engine node state (the runtime is
 * an `<Editor enabled={false}>` host, pattern: appBar useLinkedLinksData).
 * null = state unavailable → callers fail OPEN to enabled+label headings.
 * With `bands` set, buckets by child `tierId` (unmatched → NO_TIER_KEY);
 * legacy mode buckets by the 4-value `tier` prop.
 * MIRRORED in the editor twin (main-frontend sections/Sponsors) — keep in sync.
 * Note: a logo-less AND nameless child (renders nothing) still counts toward
 * its band — near-impossible via the cockpit (sponsor name is required).
 */
function useTierBandCounts(
  bands: SponsorTierBand[] | null,
): Record<string, number> | null {
  const nodeId = useContext(NodeContext);
  return useEditor((state) => {
    if (!nodeId) return null;
    const node = state.nodes[nodeId];
    if (!node) return null;
    const counts: Record<string, number> = {};
    if (bands) {
      bands.forEach((b) => (counts[b.id] = 0));
      counts[NO_TIER_KEY] = 0;
    } else {
      counts.gold = 0;
      counts.silver = 0;
      counts.bronze = 0;
      counts.none = 0;
    }
    for (const childId of node.data.nodes) {
      const p = state.nodes[childId]?.data.props ?? {};
      if (p.isVisible === false) continue;
      if (bands) {
        const key =
          typeof p.tierId === "string" && p.tierId in counts && p.tierId !== NO_TIER_KEY
            ? p.tierId
            : NO_TIER_KEY;
        counts[key] = (counts[key] ?? 0) + 1;
      } else {
        const tier =
          typeof p.tier === "string" && p.tier in TIER_ORDER
            ? (p.tier as SponsorTier)
            : "none";
        counts[tier] = (counts[tier] ?? 0) + 1;
      }
    }
    return counts;
  });
}

/**
 * Sponsors / logo-wall section: a grid of SponsorsChild logos. Markup mirrors
 * the editor's sections/Sponsors — keep the two in sync.
 */
export function Sponsors(props: RuntimePropsWithChildren) {
  // Hooks before the visibility early-return (rules of hooks).
  const { currentLang, primaryLanguage } = useWBLang();
  const { currentColorPalette } = useWBTheme();
  const tierBands = parseTierBands(props.tierBands);
  const bandCounts = useTierBandCounts(tierBands);
  if (!isVisible(props.isVisible as boolean | undefined)) return null;
  const padding =
    (props.padding as string | undefined) ?? "p-4 wb-md:p-6 wb-lg:p-8";
  const sectionMargin = (props.sectionMargin as string | undefined) ?? "py-0";
  const gridColumns =
    (props.gridColumns as "2" | "3" | "4" | "5" | "6" | undefined) ?? "4";
  const distEntries = (props.distEntries as string | undefined) ?? "gap-6";

  return (
    <ThemeSectionContainer
      sectionHeight={props.sectionHeight as number | undefined}
      contentAlignment={
        props.contentAlignment as "top" | "center" | "bottom" | undefined
      }
      backgroundColor={props.backgroundColor as string | undefined}
      padding={padding}
      sectionMargin={sectionMargin}
      border={props.border as boolean | undefined}
      borderColor={props.borderColor as string | undefined}
      borderWidth={props.borderWidth as string | undefined}
      borderStyle={props.borderStyle as string | undefined}
      borderSides={props.borderSides as BorderSide[] | undefined}
      borderRadius={props.borderRadius as string | undefined}
    >
      <motion.div
        {...SECTION_REVEAL_PROPS}
        className={classNames(
          "w-full max-w-275 grid items-center",
          // Mirror the editor (sections/Sponsors renders getGridClasses(gridColumns, "100%")):
          // the logo wall always spans the full content width.
          getGridClasses(gridColumns, "100%"),
          distEntries,
        )}
      >
        {tierBands
          ? // Dynamic band headings (configured tiers) — full-width grid items
            // ordered one below their band's logos; the implicit no-tier band
            // renders last and never gets a heading.
            tierBands.map((band, index) => {
              if (band.enabled !== true) return null;
              // Orphan-heading fix: an enabled band with zero (visible)
              // logos renders no heading (selection filter emptied it).
              if (bandCounts && bandCounts[band.id] === 0) return null;
              const label = resolveText(
                band.label,
                currentLang,
                primaryLanguage,
              );
              if (!label) return null;
              return (
                <p
                  key={band.id}
                  style={{
                    order: bandHeadingOrder(index),
                    color: currentColorPalette.secondary,
                  }}
                  className="col-span-full text-xs font-medium uppercase tracking-widest"
                >
                  {label}
                </p>
              );
            })
          : /* Legacy tier heading rows (sections-expansion W1) — byte-kept
               for pages baked before configurable tiers. */
            TIER_HEADINGS.map(({ tier, enabledKey, labelKey }) => {
              if (props[enabledKey] !== true) return null;
              if (bandCounts && bandCounts[tier] === 0) return null;
              const label = resolveText(
                props[labelKey] as LangText | undefined,
                currentLang,
                primaryLanguage,
              );
              if (!label) return null;
              return (
                <p
                  key={tier}
                  style={{
                    order: TIER_ORDER[tier] - 1,
                    color: currentColorPalette.secondary,
                  }}
                  className="col-span-full text-xs font-medium uppercase tracking-widest"
                >
                  {label}
                </p>
              );
            })}
        <SponsorTierBandsContext.Provider value={tierBands}>
          {props.children}
        </SponsorTierBandsContext.Provider>
      </motion.div>
    </ThemeSectionContainer>
  );
}

/** A single sponsor logo (optionally linked). Logo-less sponsors render their
 *  NAME as a text tile (alt carries the sponsor name for generated children);
 *  logo-less AND nameless renders nothing. MIRRORED in the editor twin
 *  (main-frontend children/Sponsors) — keep in sync. */
export function SponsorsChild(props: Record<string, unknown>) {
  // Hooks before the early returns (rules of hooks).
  const { currentColorPalette } = useWBTheme();
  const tierBands = useContext(SponsorTierBandsContext);
  if (!isVisible(props.isVisible as boolean | undefined)) return null;
  const src = sanitizeImageSrc(props.src as string | undefined);
  const alt = ((props.alt as string | undefined) ?? "").trim();
  if (!src && !alt) return null;
  const href = (props.href as string | undefined)?.trim();
  const grayscale = (props.grayscale as boolean | undefined) ?? true;
  const logoHeight = (props.logoHeight as string | undefined) ?? "h-12";

  // Band placement: dynamic (configured tiers via section context, matched
  // by tierId — unmatched/absent → trailing no-tier band) with the legacy
  // 4-value `tier` map as the no-config path.
  let order: number;
  let scale: string | undefined;
  if (tierBands) {
    const index = tierBands.findIndex((b) => b.id === props.tierId);
    const band = index >= 0 ? tierBands[index] : undefined;
    if (band !== undefined) {
      order = bandLogoOrder(index);
      scale = BAND_STYLE_SCALE[band.style ?? "standard"];
    } else {
      order = noTierOrder(tierBands.length);
      scale = undefined;
    }
  } else {
    const tier =
      ((props.tier as SponsorTier | undefined) ?? "none") in TIER_ORDER
        ? ((props.tier as SponsorTier | undefined) ?? "none")
        : "none";
    order = TIER_ORDER[tier];
    scale = TIER_SCALE[tier];
  }

  const img = src ? (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      className={classNames(
        "w-full object-contain mx-auto transition-all",
        logoHeight,
        scale,
        grayscale ? "grayscale opacity-70 hover:grayscale-0 hover:opacity-100" : undefined,
      )}
    />
  ) : (
    <span
      className={classNames(
        "w-full flex items-center justify-center text-center px-2 font-medium mx-auto transition-all",
        logoHeight,
        scale,
        grayscale ? "opacity-70 hover:opacity-100" : undefined,
      )}
      style={{ color: currentColorPalette.primary }}
    >
      {alt}
    </span>
  );

  // External links only — sanitizeImageSrc-style guard: http(s) or same-site path.
  const safeHref =
    href && (/^https?:\/\//i.test(href) || href.startsWith("/")) ? href : undefined;

  // Public attachments (Wave A) — baked, showOnWebsite-filtered. Rendered
  // OUTSIDE the logo link (a chip click must download, not follow the
  // sponsor's website link).
  const chips = (
    <>
      {/* Published custom fields — mirror of the editor twin. Outside the logo
          link for the same reason the chips are. */}
      <CustomFieldLines
        customFields={props.customFields}
        className="mt-1 flex flex-col items-center gap-1"
      />
      <AttachmentChips attachments={props.attachments} className="mt-1 justify-center" />
    </>
  );

  // The tier `order` band must sit on the GRID ITEM (this root element).
  return safeHref ? (
    <div style={{ order }}>
      <a
        href={safeHref}
        target="_blank"
        rel="noreferrer noopener"
        className="block"
      >
        {img}
      </a>
      {chips}
    </div>
  ) : (
    <div style={{ order }}>
      {img}
      {chips}
    </div>
  );
}
